import React, { useEffect, useRef, useState } from "react";
import { G, UI, useStore, useIsGM } from "../store";
import { create, update, remove, toast } from "../net";
import type { Track } from "../../shared/types";
import { AssetPicker, ArtPanel } from "./Art";
import { confirm, prompt, Field } from "../util";

export function JukeboxPanel() {
  const gm = useIsGM();
  const tracks = useStore(G, s => s.recs.track);
  const volume = useStore(UI, s => s.volume);
  const list = Object.values(tracks).sort((a, b) => a.order - b.order);
  const play = (t: Track) => update("track", t.id, { playing: true, startedAt: Date.now() });
  const stop = (t: Track) => update("track", t.id, { playing: false });
  const add = (src: string, name: string) => create("track", { src, name: name.replace(/\.[a-z0-9]+$/i, "") || "Track", order: list.length });
  return (
    <div className="jukebox">
      <div className="art-bar">
        <span>🔊 My volume</span>
        <input type="range" min={0} max={100} value={volume} onChange={e => UI.set({ volume: +e.target.value })} />
        <span className="dim">{volume}%</span>
      </div>
      {gm && (
        <div className="art-bar">
          <AssetPicker kind="audio" label="＋ Add from library" onPick={add} />
          <button onClick={async () => { const u = await prompt("Audio URL (https://…mp3/ogg)"); if (u && /^https?:\/\//.test(u)) add(u, (await prompt("Track name", "Track")) || "Track"); }}>＋ URL</button>
          <button onClick={() => list.filter(t => t.playing).forEach(stop)}>■ Stop all</button>
        </div>
      )}
      <div className="tracks">
        {list.map(t => (
          <div key={t.id} className={`track${t.playing ? " playing" : ""}`}>
            {gm ? (
              <button className="play" onClick={() => (t.playing ? stop(t) : play(t))}>{t.playing ? "■" : "▶"}</button>
            ) : <span className="play">{t.playing ? "♪" : ""}</span>}
            {gm ? <Field className="grow" value={t.name} onCommit={v => update("track", t.id, { name: v })} /> : <span className="grow">{t.name}</span>}
            {gm && (
              <>
                <label className="chk small" title="Loop"><input type="checkbox" checked={t.loop} onChange={e => update("track", t.id, { loop: e.target.checked })} />↻</label>
                <input type="range" min={0} max={100} value={t.volume} title="Track volume (for everyone)" onChange={e => update("track", t.id, { volume: +e.target.value })} />
                <button className="mini danger" onClick={async () => { if (await confirm(`Remove ${t.name}?`)) remove("track", t.id); }}>✕</button>
              </>
            )}
          </div>
        ))}
        {!list.length && <div className="dim pad">{gm ? "Add music and ambience. Everyone hears what you play, in sync." : "The GM hasn't added any music."}</div>}
      </div>
      {gm && (
        <details className="pad-x">
          <summary>Audio library</summary>
          <ArtPanel kind="audio" />
        </details>
      )}
    </div>
  );
}

/** Plays whatever the GM has on; mounted once per game. */
export function AudioEngine() {
  const tracks = useStore(G, s => s.recs.track);
  const volume = useStore(UI, s => s.volume);
  const els = useRef(new Map<string, HTMLAudioElement>());
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const live = new Set<string>();
    for (const t of Object.values(tracks)) {
      if (!t.playing || !t.src) continue;
      live.add(t.id);
      let a = els.current.get(t.id);
      const fresh = !a || a.dataset.src !== t.src || a.dataset.started !== String(t.startedAt);
      if (!a) {
        a = new Audio();
        a.preload = "auto";
        els.current.set(t.id, a);
        a.onended = () => { if (!a!.loop) els.current.delete(t.id); };
      }
      a.loop = t.loop;
      a.volume = Math.max(0, Math.min(1, (t.volume / 100) * (volume / 100)));
      if (fresh) {
        a.dataset.src = t.src;
        a.dataset.started = String(t.startedAt);
        a.src = t.src;
        const el = a;
        const seek = () => {
          const off = Math.max(0, (Date.now() - (t.startedAt || Date.now())) / 1000);
          if (el.duration && isFinite(el.duration)) {
            if (off < el.duration) el.currentTime = off;
            else if (t.loop) el.currentTime = off % el.duration;
            else return;
          }
          el.play().then(() => setBlocked(false)).catch(() => setBlocked(true));
        };
        if (el.readyState >= 1) seek(); else el.addEventListener("loadedmetadata", seek, { once: true });
      }
    }
    for (const [id, a] of els.current) if (!live.has(id)) { a.pause(); a.src = ""; els.current.delete(id); }
  }, [tracks, volume]);

  useEffect(() => () => { for (const a of els.current.values()) { a.pause(); a.src = ""; } }, []);

  if (!blocked) return null;
  return (
    <button className="audio-unblock" onClick={() => {
      for (const a of els.current.values()) a.play().catch(() => {});
      setBlocked(false);
    }}>🔇 Click to enable game audio</button>
  );
}
